import { useUser } from "../contexts/UserContext";
import { supabase } from "../supabase/supabaseClient";
import { useState, useEffect } from "react";
import { usePosts } from "../contexts/PostContext";

export default function MyPage() {
  const { user, setUser, logout } = useUser();
  const { posts } = usePosts();
  const [editing, setEditing] = useState(false);
  const [newNickname, setNewNickname] = useState(user?.nickname || "");
  const [error, setError] = useState("");
  const [evidence, setEvidence] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [myPosts, setMyPosts] = useState<any[]>([]);
  const [createdAt, setCreatedAt] = useState("");

  const forbiddenWords = [
  "관리자", "운영자", "admin", "system", "moderator", "sysop", "운영팀", "master", "staff",
  "카카오", "구글", "삼성", "네이버", "청와대", "경찰", "국회", "대통령", "헌법재판소", "검찰",
  "대표", "교수", "선생", "의사", "변호사", "판사", "의원", "기자", "장관",
  "fuck", "shit", "bitch", "asshole", "시발", "좆", "병신", "씨발", "개새끼", "ㅅㅂ", "ㅂㅅ", "ㄱㅅ",
  "멍청이", "정신병", "장애인", "미친놈", "쓰레기", "개새", "개새끼", "개자식", "개새꺄", "개새끼야",
  "개새끼야", "개새끼야", "개새끼야",
  "알림", "시스템", "공지", "익명", "unknown", "anonymous", "비공개", "ABlind", "A-Blind", "에이블라인드", 
  "A_Blind", "ABLIND", "A_BLIND", "AB", "에이블라인드", "A-BLIND", "Ablind", "A Blind", "에이 블라인드",
];

function isNicknameForbidden(nickname: string) {
  const lowerNickname = nickname.toLowerCase();
  for (const word of forbiddenWords) {
    if (lowerNickname.includes(word.toLowerCase())) {
      return true;
    }
  }
  // 개인정보 패턴(폰/이메일 등)
  const phoneOrEmailRegex = /\d{3}[- ]?\d{3,4}[- ]?\d{4}|@[a-z]/i;
  if (phoneOrEmailRegex.test(nickname)) return true;
  // 너무 짧거나 특수문자만
  if (/^[^a-zA-Z0-9가-힣]+$/.test(nickname) || nickname.replace(/[\s_]/g, "").length < 2) return true;
  return false;
}

  useEffect(() => {
    if (user) {
      const filtered = posts.filter(p => p.author_id === user.id);
      setMyPosts(filtered);
      supabase.auth.getUser().then(({ data: { user: authUser } }) => {
        if (authUser?.created_at) {
          setCreatedAt(new Date(authUser.created_at).toLocaleDateString());
        }
      });
    }
  }, [user, posts]);

  async function handleNicknameSave() {
    if (!user || !newNickname.trim()) return;
    setError("");

    // 닉네임이 금지된 단어인지 확인하는 함수
    if (isNicknameForbidden(newNickname.trim())) {
    setError("사용할 수 없는 닉네임입니다.\n(운영자/시스템/욕설/브랜드/개인정보 등 금지)");
    return;
  }

    // 닉네임 중복 검사 (자기 자신 제외)
    const { data: dup, error: checkError } = await supabase
      .from("profiles")
      .select("id")
      .eq("nickname", newNickname.trim())
      .neq("id", user.id);

    if (checkError) {
      setError("닉네임 확인 중 오류: " + checkError.message);
      return;
    }

    if (dup && dup.length > 0) {
      setError("이미 사용 중인 닉네임입니다.");
      return;
    }

    // 닉네임 업데이트
    const { error } = await supabase
      .from("profiles")
      .update({ nickname: newNickname.trim() })
      .eq("id", user.id);

    if (error) {
      if (
        error.message.includes("duplicate key value") ||
        error.message.includes("unique constraint")
      ) {
        setError("이미 사용 중인 닉네임입니다.");
      } else {
        setError("저장 실패: " + error.message);
      }
      return;
    }

    setUser({ ...user, nickname: newNickname.trim() });
    setEditing(false);
  }

  async function handleUpgrade() {
    if (!user || !evidence) return;
    setSubmitting(true);

    try {
      const { data, error } = await supabase.storage
        .from("evidence")
        .upload(`${user.id}/${Date.now()}_${evidence.name}`, evidence, {
          upsert: true,
          contentType: evidence.type,
        });

      if (error) throw error;

      const publicUrl = supabase.storage.from("evidence").getPublicUrl(data.path).data.publicUrl;

      const { error: dbError } = await supabase
        .from("profiles")
        .update({ evidence_url: publicUrl, evidence_status: "pending" })
        .eq("id", user.id);

      if (dbError) throw dbError;

      setUser({ ...user, evidence_url: publicUrl, evidence_status: "pending" });
      alert("등업 신청 완료! 관리자의 승인을 기다려주세요.");
    } catch (err: any) {
      alert("실패: " + (err.message || err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDeleteAccount() {
    const confirm = window.confirm("정말 탈퇴하시겠습니까?");
    if (!confirm || !user) return;

    const { error } = await supabase
      .from("profiles")
      .update({ deleted: true, deleted_at: new Date().toISOString() })
      .eq("id", user.id);

    if (!error) {
      await logout();
      alert("탈퇴가 완료되었습니다.");
    } else {
      alert("탈퇴 실패: " + error.message);
    }
  }

  if (!user) return <div className="mt-20 text-center">로그인이 필요합니다.</div>;

  return (
    <div className="max-w-2xl mx-auto mt-16 bg-white rounded shadow p-8">
      <h2 className="font-bold text-2xl mb-6">마이페이지</h2>

      <div className="space-y-2 mb-6">
        <div><strong>이메일:</strong> {user.email}</div>

        <div className="flex items-center gap-2">
          <strong>닉네임:</strong>
          {editing ? (
            <>
              <input
                className="border px-2 py-1 rounded"
                value={newNickname}
                onChange={(e) => setNewNickname(e.target.value)}
              />
              <button onClick={handleNicknameSave} className="text-sm text-green-600">저장</button>
              <button onClick={() => setEditing(false)} className="text-sm text-gray-500">취소</button>
            </>
          ) : (
            <>
              <span>{user.nickname}</span>
              <button onClick={() => setEditing(true)} className="text-sm text-blue-500 underline">수정</button>
            </>
          )}
        </div>
        {error && <div className="text-red-500 text-sm mt-1">{error}</div>}

        <div><strong>유형:</strong> {user.userType}</div>
        <div><strong>등급:</strong> {user.level === "full" ? "정회원" : "승인 대기중(48시간이내승인)"}</div>
        {createdAt && <div><strong>가입일:</strong> {createdAt}</div>}
      </div>

      {user.level === "pending" && (
        <div className="mb-6">
          <h3 className="font-semibold mb-2">등업 신청</h3>
          <input
            type="file"
            accept="image/*,.pdf"
            onChange={(e) => setEvidence(e.target.files?.[0] || null)}
            disabled={submitting}
          />
          <button
            className="ml-2 bg-black text-white px-4 py-1 rounded"
            onClick={handleUpgrade}
            disabled={submitting}
          >
            {submitting ? "신청중..." : "제출"}
          </button>
          {user.evidence_url && (
            <div className="mt-2 text-sm">
              제출한 파일: <a href={user.evidence_url} target="_blank" rel="noreferrer noopener" className="underline text-blue-600">보기</a>
            </div>
          )}
        </div>
      )}

      <hr className="my-6" />

      <div>
        <h3 className="font-bold mb-3">내가 쓴 글 ({myPosts.length})</h3>
        {myPosts.length > 0 ? (
          <ul className="space-y-2 text-sm text-gray-700">
            {myPosts.map((p) => (
              <li key={p.id}>
                <strong>{p.title}</strong>{" "}
                <span className="text-gray-400">({new Date(p.date).toLocaleDateString()})</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-gray-400 text-sm">작성한 게시글이 없습니다.</div>
        )}
      </div>

      <hr className="my-6" />

      <div className="text-right">
        <button onClick={handleDeleteAccount} className="text-red-500 text-sm underline">
          계정 탈퇴
        </button>
      </div>
    </div>
  );
}
